'use client';

import NextImage, { ImageLoaderProps, ImageProps } from 'next/image';

import { buildConfig } from '~/build-config/reader';
import bcCdnImageLoader, { type ImageLoaderPreset } from '~/lib/cdn-image-loader';

function shouldUseLoaderProp(props: ImageProps): boolean {
  if (typeof props.src !== 'string') return false;

  const { src } = props;

  // Cloudinary upload URLs need the loader to inject width transformations
  if (src.includes('res.cloudinary.com/') && src.includes('/image/upload/')) return true;
  if (src.startsWith('https://storage.googleapis.com/')) return true;

  return buildConfig.get('urls').cdnUrls.some((cdn) => src.startsWith(`https://${cdn}`));
}

export interface UnurImageProps extends ImageProps {
  preset?: ImageLoaderPreset;
}

/**
 * This component should be used in place of Next's `Image` component for images from the
 * BigCommerce platform, which will reduce load on the Next.js application for image assets.
 *
 * It defaults to use the default loader in Next.js if it's an image not from the BigCommerce CDN.
 *
 * @returns {React.ReactElement} The `<Image>` component
 */
export const Image = ({ preset = 'default', ...props }: UnurImageProps) => {
  const loader = shouldUseLoaderProp(props)
    ? (loaderProps: ImageLoaderProps) => bcCdnImageLoader({ ...loaderProps, preset })
    : undefined;

  return <NextImage loader={loader} {...props} />;
};
